import type { ConfigType, Dayjs, PluginFunc } from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'

import {
  permanentDstLabel,
  resolveDisplayZone,
  resolveWallClockZone,
  zoneAbbreviation,
} from './permanent-dst'

/**
 * Makes every dayjs zone conversion in the app survive a runtime whose IANA
 * database has not caught up with a province that abolished its clock change.
 *
 * A plugin rather than an edit at each call site: `.tz()` is used in something
 * like seventy places across web and mobile — game rows, chat stamps, pickers,
 * CSV exports — and for a viewer in British Columbia every one of them is an
 * hour out from 2026-11-01 until their browser ships the new rules. Correct by
 * default beats correct where someone remembered. `ts-utils/permanent-dst`
 * holds the rules themselves and decides when nothing needs doing.
 *
 * Two substitutions, matching the two directions `.tz()` runs in:
 *
 * - reading an instant in a zone, where the instant picks the rules
 * - carrying a wall clock into a zone (`dayjs.tz(input, zone)`, or
 *   `keepLocalTime`), where the typed wall clock picks them
 *
 * The zone we substitute in is right about the offset but not about the name —
 * IANA labels a permanent summer offset after the standard zone that shares
 * it — so `format('z')` gets the label put back afterwards.
 *
 * Registered wherever the timezone plugin is (the house pattern: at the top of
 * the module that uses it, so the file works standalone in a unit test) and in
 * each app's entry point, which covers the screens that reach for dayjs
 * directly. `dayjs.extend` is idempotent, so extending it repeatedly is free.
 */

/** The instance's own wall clock, read as though it were UTC. */
const wallClockMs = (instance: Dayjs): number =>
  Date.UTC(
    instance.year(),
    instance.month(),
    instance.date(),
    instance.hour(),
    instance.minute(),
    instance.second(),
    instance.millisecond()
  )

interface ZoneCarrier {
  $x?: { $timezone?: string; $permanentDstLabel?: string }
}

const permanentDst: PluginFunc = (_options, DayjsClass, dayjsFactory) => {
  //  The timezone plugin owns the `tz` we are wrapping and `format`'s `z`
  //  token, so it has to be installed before we take a reference to either.
  //  Extending it here rather than assuming registration order: a module that
  //  extends it later gets a no-op, one that extended it earlier is unaffected.
  dayjsFactory.extend(utcPlugin)
  dayjsFactory.extend(timezonePlugin)

  const proto = DayjsClass.prototype as Dayjs & ZoneCarrier
  //  Through the plugin's own signature rather than the published one, which
  //  requires a zone and marks the factory's `tz` read-only.
  const originalTz = proto.tz as unknown as (
    this: Dayjs,
    timezone?: string,
    keepLocalTime?: boolean
  ) => Dayjs
  const originalFormat = proto.format

  const remember = (result: Dayjs, realZone: string, at: number): Dayjs => {
    const label = permanentDstLabel(realZone, at)
    const carrier = result as Dayjs & ZoneCarrier
    if (label !== null && carrier.$x) {
      carrier.$x.$permanentDstLabel = label
    }
    return result
  }

  proto.tz = function tz(
    this: Dayjs,
    timezone?: string,
    keepLocalTime?: boolean
  ) {
    if (typeof timezone !== 'string') {
      return originalTz.call(this, timezone, keepLocalTime)
    }

    const at = keepLocalTime ? wallClockMs(this) : this.valueOf()
    const zone = keepLocalTime
      ? resolveWallClockZone(timezone, at)
      : resolveDisplayZone(timezone, at)

    const result = originalTz.call(this, zone, keepLocalTime)
    return remember(result, timezone, result.valueOf())
  }

  proto.format = function format(this: Dayjs & ZoneCarrier, template?: string) {
    const formatted = originalFormat.call(this, template)
    const label = this.$x?.$permanentDstLabel
    const substituted = this.$x?.$timezone

    if (!label || !substituted) {
      return formatted
    }

    //  Swap the substitute zone's abbreviation for the one people there use.
    //  A no-op unless the format asked for `z`, since nothing else in a
    //  formatted date reads as a zone name.
    const abbreviation = zoneAbbreviation(substituted, this.valueOf())
    return abbreviation === '' || abbreviation === label
      ? formatted
      : formatted.split(abbreviation).join(label)
  }

  const originalFactoryTz = dayjsFactory.tz

  const factoryTz = (
    input?: ConfigType,
    arg1?: string,
    arg2?: string
  ): Dayjs => {
    //  dayjs.tz(input, timezone) and dayjs.tz(input, format, timezone) — the
    //  zone is the last string given, exactly as the timezone plugin reads it.
    const timezone = arg2 || arg1

    if (typeof timezone !== 'string') {
      return originalFactoryTz(input, arg1 as string, arg2 as string)
    }

    //  This form reads `input` as a WALL CLOCK in `timezone`, so the wall clock
    //  is what decides which rules apply. Parsing it as UTC yields exactly that
    //  clock in milliseconds.
    const typedAt = dayjsFactory.utc(input).valueOf()
    const zone = resolveWallClockZone(timezone, typedAt)

    const result =
      arg2 === undefined
        ? originalFactoryTz(input, zone)
        : originalFactoryTz(input, arg1 as string, zone)

    return remember(result, timezone, result.valueOf())
  }

  //  `guess` and `setDefault` hang off the function the plugin installed, so
  //  they are carried over onto the wrapper.
  const factory = dayjsFactory as unknown as { tz: typeof originalFactoryTz }
  factory.tz = Object.assign(factoryTz, originalFactoryTz)
}

export default permanentDst
